import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Languages, Plus, Trash2 } from 'lucide-react';
import {
  type Translations,
  getWorkflowLocalization,
  setWorkflowLocalization,
} from '@/api/wfLocalizations';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';

interface Props {
  workflowTemplateId: string;
  workflowName: string;
  onClose: () => void;
}

const LOCALES = [
  { code: 'es', labelKey: 'wfLocalizations.locale.es' },
  { code: 'en', labelKey: 'wfLocalizations.locale.en' },
  { code: 'pt-BR', labelKey: 'wfLocalizations.locale.ptBR' },
];

interface Pair {
  key: string;
  value: string;
}

function toPairs(t: Translations | null): Pair[] {
  if (!t) return [];
  return Object.entries(t).map(([key, value]) => ({
    key,
    value: typeof value === 'string' ? value : JSON.stringify(value),
  }));
}

export function WorkflowLocalizationsModal({ workflowTemplateId, workflowName, onClose }: Props) {
  const { t } = useTranslation();
  const [locale, setLocale] = useState('es');
  const [pairs, setPairs] = useState<Pair[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setSaved(false);
    getWorkflowLocalization(workflowTemplateId, locale)
      .then((data) => setPairs(toPairs(data)))
      .catch(() => setPairs([]))
      .finally(() => setLoading(false));
  }, [workflowTemplateId, locale]);

  useEffect(() => {
    load();
  }, [load]);

  function updatePair(idx: number, patch: Partial<Pair>) {
    setPairs((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  }

  function addPair() {
    setPairs((prev) => [...prev, { key: '', value: '' }]);
  }

  function removePair(idx: number) {
    setPairs((prev) => prev.filter((_, i) => i !== idx));
  }

  async function save() {
    const translations: Translations = {};
    for (const p of pairs) {
      const k = p.key.trim();
      if (k) translations[k] = p.value;
    }
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await setWorkflowLocalization({ workflowTemplateId, locale, translations });
      setSaved(true);
    } catch (err) {
      setError((err as Error)?.message ?? 'Failed to save translations');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[92vh] w-full max-w-lg flex-col rounded-lg border bg-card shadow-xl">
        <header className="flex items-start justify-between border-b px-5 py-4">
          <div className="min-w-0">
            <h3 className="flex items-center gap-2 text-lg font-semibold">
              <Languages className="h-5 w-5" />
              {t('wfLocalizations.title')}
            </h3>
            <p className="truncate text-xs text-[var(--color-lop-text-muted)]">{workflowName}</p>
          </div>
          <div className="w-40">
            <Select value={locale} onChange={(e) => setLocale(e.target.value)} data-testid="wfloc-locale">
              {LOCALES.map((l) => (
                <option key={l.code} value={l.code}>
                  {t(l.labelKey)}
                </option>
              ))}
            </Select>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-5">
          <p className="mb-3 text-xs text-[var(--color-lop-text-muted)]">{t('wfLocalizations.hint')}</p>
          {error && <p className="mb-3 text-sm text-red-400">{error}</p>}
          {saved && <p className="mb-3 text-sm text-emerald-400">{t('wfLocalizations.saved')}</p>}
          {loading ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : (
            <>
              {pairs.length === 0 && (
                <p className="mb-3 text-sm text-muted-foreground">{t('wfLocalizations.empty')}</p>
              )}
              <div className="space-y-2">
                {pairs.map((p, idx) => (
                  <div key={idx} className="flex gap-2" data-testid="wfloc-pair">
                    <Input
                      value={p.key}
                      onChange={(e) => updatePair(idx, { key: e.target.value })}
                      placeholder={t('wfLocalizations.keyPlaceholder')}
                      className="w-2/5 font-mono text-xs"
                      data-testid="wfloc-key"
                    />
                    <Input
                      value={p.value}
                      onChange={(e) => updatePair(idx, { value: e.target.value })}
                      placeholder={t('wfLocalizations.valuePlaceholder')}
                      data-testid="wfloc-value"
                    />
                    <button
                      type="button"
                      onClick={() => removePair(idx)}
                      className="shrink-0 rounded p-2 text-[var(--color-lop-text-muted)] hover:bg-white/5"
                      data-testid="wfloc-remove"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
              <Button size="sm" variant="ghost" onClick={addPair} className="mt-3" data-testid="wfloc-add">
                <Plus className="mr-1 h-4 w-4" />
                {t('wfLocalizations.addKey')}
              </Button>
            </>
          )}
        </div>

        <footer className="flex justify-end gap-2 border-t px-5 py-4">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            {t('common.close')}
          </Button>
          <Button size="sm" onClick={save} disabled={saving || loading} data-testid="wfloc-save">
            {t('wfLocalizations.save')}
          </Button>
        </footer>
      </div>
    </div>
  );
}
